import {Injectable} from '@angular/core';
import {HttpClient, HttpContext, HttpErrorResponse} from '@angular/common/http';
import {SKIP_ERROR_REPORTING} from '../interceptors/app.interceptor';
import {
  AlarmEvent,
  EventQuery,
  EventQueryResult,
  ValueQuery,
  ValueQueryResult,
} from '../../shared/models/alarm';
import {SessionService} from './session.service';
import {catchError} from 'rxjs/operators';
import {BehaviorSubject, Observable, throwError} from 'rxjs';
import {AppState} from '../../app/app.state';
import {Store} from '@ngrx/store';
import {AlarmsActiveEventsFetched, AlarmsEventAcknowledged, AlarmsEventCleared, AlarmsEventFetched} from '../actions/alarms.actions';
import {API_BASE_URL} from './env.service';

const ALARM_FETCH_INTERVAL = 12000;
const ALARM_ACTIVE_EVENTS_PAGE_SIZE = 5000; // TEMPORARY (PLAT-574): matches backend's temporary Qty=5000 override (motion-backend PR #69)

@Injectable({
  providedIn: 'root',
})
export class AlarmsService {
  private startedFetchingUserAlarms: boolean = false;

  activeEvents$: BehaviorSubject<AlarmEvent[]> = new BehaviorSubject<AlarmEvent[]>([]);
  private activeEvents: AlarmEvent[] = [];


  constructor(
    private http: HttpClient,
    private session: SessionService,
    private store: Store<AppState>,
  ) {
    this.store.select('alarms').subscribe(
      (data) => {
        if (data.activeEvents) {
          this.activeEvents = data.activeEvents;
          this.activeEvents$.next(this.activeEvents);
        }
      },
    );

    this.fetchUserAlarmEvents();
  }


  /**
   *
   * @param options
   */
  fetchAlarmValues$(options: ValueQuery): Observable<ValueQueryResult> {
    return this.http.get<ValueQueryResult>(`${API_BASE_URL}/alarms/values`, {
      params: {...options},
    });
  }


  /**
   *
   * @param options
   */
  fetchAlarmEvents$(options: EventQuery): Observable<EventQueryResult> {
    return this.http.get<EventQueryResult>(`${API_BASE_URL}/alarms/events`, {
      params: {...options},
    });
  }


  /**
   * Do not call this more than once per session! The only repeated calls should be from
   * within this function itself.
   *
   * @param force {boolean} A way to prevent this function from being called more than
   *  once by accident, except when being called based on a timeout from within the
   *  function itself
   * @private
   */
  private fetchUserAlarmEvents(force?: boolean): void {
    if (this.startedFetchingUserAlarms && !force) {
      return;
    }

    this.startedFetchingUserAlarms = true;

    if (this.session.hasAuthenticated()) {
      const eq: EventQuery = {
        ObjectType: 'tile,hub,device',
        ActiveOnly: 'true',
        Qty: `${ALARM_ACTIVE_EVENTS_PAGE_SIZE}`, // TODO: support pagination
      };

      this.fetchAlarmEvents$(eq).subscribe({
        next: (data) => {
          this.store.dispatch(new AlarmsActiveEventsFetched(data?.ResultSet ?? []));
          setTimeout(this.fetchUserAlarmEvents.bind(this, true), ALARM_FETCH_INTERVAL);
        },
        error: () => {
          setTimeout(this.fetchUserAlarmEvents.bind(this, true), ALARM_FETCH_INTERVAL);
        },
      });
    } else {
      setTimeout(this.fetchUserAlarmEvents.bind(this, true), 500);
    }
  }


  fetchAlarmEvent$(eventUuid: string): Observable<AlarmEvent> {
    return new Observable<AlarmEvent>((subscriber) => {
      const eq: EventQuery = {
        UUID: eventUuid,
      };

      this.fetchAlarmEvents$(eq).subscribe({
        next: (data) => {
          const alarmEvent: AlarmEvent = data.ResultSet[0];
          this.store.dispatch(new AlarmsEventFetched(alarmEvent));

          subscriber.next(alarmEvent);
          subscriber.complete();
        },
        error: () => {
          subscriber.error();
        },
      });
    });
  }


  acknowledgeEvent$(eventUuid: string): Observable<AlarmEvent> {
    return new Observable<AlarmEvent>(
      subscriber => {
        // 409 means already acknowledged (e.g. by another user/session), handled as success in handleError below
        this.http.post<AlarmEvent>(`${API_BASE_URL}/alarms/events/${eventUuid}/acknowledgment`, null, {
          context: new HttpContext().set(SKIP_ERROR_REPORTING, (err) => err.status === 409),
        }).pipe(
          catchError((err) => this.handleError(err)),
        ).subscribe(
          (data) => {
            subscriber.next(data);
            this.store.dispatch(new AlarmsEventAcknowledged(data));

            subscriber.complete();
          },
          (error) => {
            subscriber.error(error);
          },
        );
      },
    );
  }


  clearEvent$(eventUuid: string): Observable<AlarmEvent> {
    return new Observable<any>(
      subscriber => {
        // 409 means already cleared (e.g. by another user/session), handled as success in handleError below
        this.http.post<AlarmEvent>(`${API_BASE_URL}/alarms/events/${eventUuid}/clearance`, null, {
          context: new HttpContext().set(SKIP_ERROR_REPORTING, (err) => err.status === 409),
        }).pipe(
          catchError((err) => this.handleError(err)),
        ).subscribe(
          (data) => {
            subscriber.next(data);
            this.store.dispatch(new AlarmsEventCleared(data));

            subscriber.complete();
          },
          (error) => {
            subscriber.error(error);
          },
        );
      },
    );
  }


  private handleError(error: HttpErrorResponse) {
    if (error.error instanceof ErrorEvent) {
      // client-side or network error
      console.error('An error occurred:', error.error.message);
    } else {
      if (error.status === 409) {
        if (error.url.includes('clearance')) {
          this.store.dispatch(new AlarmsEventCleared(error.error));
        } else if (error.url.includes('acknowledgment')) {
          this.store.dispatch(new AlarmsEventAcknowledged(error.error));
        }
      }
    }

    return throwError('');
  }
}
